import React from 'react';
import { Outlet } from 'react-router-dom';
import { Header } from '../components/ui/Header';
import { Footer } from '../components/ui/Footer';
import { Sidebar } from '../components/ui/Sidebar';
import { CommandPalette } from '../components/ui/CommandPalette';
import { AiAssistantModal } from '../components/ui/AiAssistantModal';
import { ToastContainer } from '../components/ui/ToastContainer';
import { Atmosphere } from '../components/brand/Atmosphere';
import { AdminSidebar } from '../components/admin/AdminSidebar';
import { AdminTopBar } from '../components/admin/AdminTopBar';
import { AdminChromeProvider } from '../components/admin/AdminChromeContext';

interface AppShellProps {
  variant: 'public' | 'customer' | 'admin';
}

/**
 * Experience shells stay independent:
 * - public: marketing / browse
 * - customer: portal after sign-in
 * - admin: Control Center — denser ops console, separate from customer shell
 */
export const AppShell: React.FC<AppShellProps> = ({ variant }) => {
  const withSidebar = variant !== 'public';
  const isAdmin = variant === 'admin';

  if (isAdmin) {
    return (
      <AdminChromeProvider>
        <div className="bc-shell bc-cc-shell min-h-screen text-ink dark:text-paper flex flex-col font-sans transition-colors selection:bg-brand-500 selection:text-white">
          <AdminTopBar />
          <div className="flex-1 flex flex-col md:flex-row w-full relative z-[1]">
            <AdminSidebar />
            <main id="main-content" className="flex-1 min-w-0 p-3 sm:p-4 lg:p-5">
              <div className="bc-cc-main min-h-[60vh]">
                <Outlet />
              </div>
            </main>
          </div>
          <ToastContainer />
        </div>
      </AdminChromeProvider>
    );
  }

  return (
    <div
      className={
        withSidebar
          ? 'bc-shell bc-shell--daylight min-h-screen text-ink dark:text-paper flex flex-col font-sans transition-colors selection:bg-brand-500 selection:text-white'
          : 'bc-shell min-h-screen bg-soft dark:bg-ink text-ink dark:text-paper flex flex-col font-sans transition-colors selection:bg-brand-500 selection:text-white'
      }
    >
      {withSidebar ? <Atmosphere mode="daylight" /> : null}
      <Header experience={variant} />
      {withSidebar ? (
        <div className="flex-1 flex flex-col md:flex-row max-w-7xl w-full mx-auto relative z-[1]">
          <Sidebar portal="customer" />
          <main id="main-content" className="flex-1 p-5 md:p-6 lg:p-8">
            <div className="bc-shell-main p-5 md:p-7 lg:p-8 min-h-[60vh]">
              <Outlet />
            </div>
          </main>
        </div>
      ) : (
        <main
          id="main-content"
          className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10 relative z-[1]"
        >
          <Outlet />
        </main>
      )}
      <Footer />
      <CommandPalette />
      {variant === 'public' && <AiAssistantModal />}
      <ToastContainer />
    </div>
  );
};
